import type { Timestep } from 'app-components/utils/date/predefinedRange';

import type { ColumnType, ReportConfiguration, ReportSubType, ReportType, RowType } from '../reportConfigurationsTypes';

const REPORT_TYPE_LABELS: Record<ReportType, string> = {
  SUMMARY: 'summary',
  TRANSACTION: 'transactions',
  COMPARISON: 'comparison',
};

const ROW_LABELS: Record<RowType, string> = {
  ACCOUNT: 'Account',
  BUSINESS: 'Business',
  CATEGORY: 'Category',
  CLIENT: 'Client',
  PROJECT: 'Project',
  PAYEE: 'Payee',
  TAG: 'Tag',
  TAX_LINE_ITEM: 'Tax line item',
  TAX_SCHEDULE: 'Tax schedule',
  TIME: 'Time',
  USAGE: 'Usage',
  NONE: 'None',
};

const COLUMN_LABELS: Record<ColumnType, string> = {
  ACCOUNT: 'Account',
  BUSINESS: 'Business',
  CLIENT: 'Client',
  PAYEE: 'Payee',
  PROJECT: 'Project',
  TAG: 'Tag',
  TIME: 'Time',
  TOTAL: 'Total',
  USAGE: 'Usage',
  NONE: 'None',
};

const EXTRA_COLUMN_LABELS: Record<string, string> = {
  customerProject: 'Clients & Projects',
  CUSTOMERPROJECT: 'Clients & Projects',
  CUSTOMER: 'Client',
};

const SUBTYPE_LABELS: Record<ReportSubType, string> = {
  BALANCE_SHEET: 'Balance sheet',
  BALANCES_OVER_TIME: 'Balances over time',
  CUSTOM: 'Custom',
  INCOME: 'Income',
  NET_INCOME: 'Net income',
  PERFORMANCE_OVER_TIME: 'Performance over time',
  SPENDING: 'Expense',
  NET_WORTH: 'Net worth',
  TAX_SCHEDULE: 'Tax schedule',
  TAX_SUMMARY: 'Tax detail',
  SAVINGS: 'Savings',
  NONE: 'None',
};

const TIMESTEP_LABELS: Partial<Record<Timestep, string>> = {
  DAY: 'day',
  WEEK: 'week',
  MONTH: 'month',
  QUARTER: 'quarter',
  YEAR: 'year',
};

const getRowLabel = (rowType: RowType, lowercase = false): string => {
  const label = ROW_LABELS[rowType] || rowType;
  return lowercase ? label.toLowerCase() : label;
};

const getColumnLabel = (columnType: ColumnType, timestep?: Timestep, lowercase = false): string | undefined => {
  if (columnType === 'NONE' || columnType === 'TOTAL') {
    return undefined;
  }

  const columnKey = columnType as string;
  let label = EXTRA_COLUMN_LABELS[columnKey] || COLUMN_LABELS[columnType] || columnKey;

  if (columnType === 'TIME') {
    label = (timestep && TIMESTEP_LABELS[timestep]) || label;
  }

  return lowercase ? label.toLowerCase() : label;
};

export const getReportTitle = (reportConfiguration: ReportConfiguration): string => {
  const { reportSubType, reportType, rowType, columnType, timestep } = reportConfiguration;

  const subtypeLabel = SUBTYPE_LABELS[reportSubType] || reportSubType;
  const reportTypeLabel = REPORT_TYPE_LABELS[reportType] || reportType.toLowerCase();

  if (reportSubType === 'TAX_SUMMARY' || reportSubType === 'TAX_SCHEDULE' || reportSubType === 'BALANCE_SHEET') {
    return subtypeLabel;
  }

  if (reportSubType === 'NET_INCOME') {
    const byColumn = getColumnLabel(columnType, timestep, true);
    const rowLabel = getRowLabel(rowType);
    return byColumn ? `${rowLabel} ${reportTypeLabel} by ${byColumn}` : `${rowLabel} ${reportTypeLabel}`;
  }

  if (reportSubType === 'SPENDING' || reportSubType === 'INCOME') {
    const byRow = rowType !== 'NONE' ? getRowLabel(rowType, true) : undefined;
    const byColumn = getColumnLabel(columnType, timestep, true);
    const byLabel = byRow || byColumn;
    return byLabel ? `${subtypeLabel} ${reportTypeLabel} by ${byLabel}` : `${subtypeLabel} ${reportTypeLabel}`;
  }

  if (reportSubType === 'SAVINGS' || reportSubType === 'NET_WORTH') {
    const byColumn = getColumnLabel(columnType, timestep, true);
    return byColumn ? `${subtypeLabel} by ${byColumn}` : subtypeLabel;
  }

  return subtypeLabel;
};
